"use client";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Checkbox } from "@/shared/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { createInquiryFormSchema, InquiryFormValues } from "../model/schema";
import { submitAnonymousInquiry } from "@/server-action/customer-service/submit-anonymous-inquiry";
import { useState, useTransition } from "react";
import { Ico20ArrowRight } from "@/shared/assets/icons/ico_20_arrow_right";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";

export default function InquiryForm() {
  const t = useTranslations();
  const schema = createInquiryFormSchema(t);
  const [isPending, startTransition] = useTransition();
  const [showTermsContent, setShowTermsContent] = useState(false);
  const [dialogState, setDialogState] = useState<{
    show: boolean;
    isSuccess: boolean;
    title: string;
  }>({
    show: false,
    isSuccess: false,
    title: "",
  });

  const form = useForm<InquiryFormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      name: "",
      contact: "",
      company: "",
      email: "",
      title: "",
      content: "",
      agreePrivacy: false,
    },
  });

  const onSubmit = (values: InquiryFormValues) => {
    startTransition(async () => {
      try {
        const result = await submitAnonymousInquiry(values);
        if (!result.ok) {
          throw new Error(result.error.message);
        }
        setDialogState({
          show: true,
          isSuccess: true,
          title: "f_com_alert_title_01",
        });
      } catch (error) {
        setDialogState({
          show: true,
          isSuccess: false,
          title: "문의 접수 실패",
        });
      }
    });
  };

  const handleDialogClose = () => {
    if (dialogState.isSuccess) {
      form.reset();
    }
    setDialogState({ show: false, isSuccess: false, title: "" });
  };

  return (
    <div className="page-container flex flex-col items-center">
      <div className="w-full max-w-[1280px]">
        <h1 className="heading-1 tb:mb-20 mb-15 text-gray-900">{t("f_cu_title")}</h1>

        <div className="tb:gap-4 tb:mb-15 mb-6 flex flex-col gap-3">
          <h3 className="heading-3-bold text-gray-900">{t("f_cu_context_1")}</h3>
          <span className="body-lg-regular text-gray-700">{t("f_cu_context_2")}</span>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="tb:hidden">
              <div className="flex flex-col space-y-5">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col gap-[6px]">
                      <FormLabel className="body-lg-bold flex gap-[2px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_name")}
                        <span className="text-red-500">*</span>
                      </FormLabel>

                      <FormControl>
                        <Input placeholder={t("f_cu_placeholder_name")} {...field} variant="public" />
                      </FormControl>
                      <FormMessage className="mt-[6px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contact"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col gap-[6px]">
                      <FormLabel className="body-lg-bold flex gap-[2px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_phone")}
                        <span className="text-red-500">*</span>
                      </FormLabel>

                      <FormControl>
                        <Input placeholder={t("f_cu_placeholder_phone")} {...field} variant="public" />
                      </FormControl>
                      <FormMessage className="mt-[6px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col gap-[6px]">
                      <FormLabel className="body-lg-bold flex gap-[2px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_hosn")}
                        <span className="text-red-500">*</span>
                      </FormLabel>

                      <FormControl>
                        <Input placeholder={t("f_cu_placeholder_hosn")} {...field} variant="public" />
                      </FormControl>
                      <FormMessage className="mt-[6px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col gap-[6px]">
                      <FormLabel className="body-lg-bold flex gap-[2px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_E-mail")}
                        <span className="text-red-500">*</span>
                      </FormLabel>

                      <FormControl>
                        <Input placeholder={t("f_cu_placeholder_E-mail")} {...field} variant="public" />
                      </FormControl>
                      <FormMessage className="mt-[6px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col gap-[6px]">
                      <FormLabel className="body-lg-bold flex gap-[2px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_title")}
                        <span className="text-red-500">*</span>
                      </FormLabel>

                      <FormControl>
                        <Input placeholder={t("f_cu_placeholder_title")} {...field} variant="public" />
                      </FormControl>
                      <FormMessage className="mt-[6px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col gap-[6px]">
                      <FormLabel className="body-lg-bold flex gap-[2px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_context")}
                        <span className="text-red-500">*</span>
                      </FormLabel>

                      <FormControl>
                        <Textarea placeholder={t("f_cu_placeholder_context")} className="resize-none" {...field} />
                      </FormControl>
                      <FormMessage className="mt-[6px]" />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col">
                  <div className="body-lg-bold mb-[10px] text-gray-900">{t("f_cu_lable_atc")}</div>

                  <FormField
                    control={form.control}
                    name="agreePrivacy"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start">
                        <FormControl>
                          <Checkbox
                            variant="public"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="mt-[2px]"
                          />
                        </FormControl>
                        <div className="flex w-full flex-col justify-between">
                          <div className="flex w-full items-center justify-between">
                            <FormLabel className="body-md-regular text-gray-900">{t("f_cu_atc_1")}</FormLabel>
                            <button
                              type="button"
                              onClick={() => setShowTermsContent(!showTermsContent)}
                              className="body-md-regular flex shrink-0 items-center gap-1 text-gray-900"
                            >
                              <Ico20ArrowRight
                                className={`transition-transform ${showTermsContent ? "rotate-[-90deg]" : "rotate-[90deg]"}`}
                              />
                            </button>
                          </div>
                          {form.formState.errors.agreePrivacy && (
                            <span className="body-sm-regular text-red-error mt-[6px]">{t("f_cu_err_agree")}</span>
                          )}
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                {showTermsContent && (
                  <div className="bg-secondary-100 animate-in fade-in-0 fade-out-0 mt-[-2] rounded-2xl p-6 duration-300">
                    <div className="body-md-regular text-gray-700">{t("f_cu_terms_title")}</div>

                    <Table className="mt-4 w-full bg-white">
                      <TableHeader className="body-md-bold h-[56px] border-b border-gray-900 text-gray-900">
                        <TableRow className="border-gray-900">
                          <TableHead variant={"public"} className="body-md-bold text-center whitespace-pre-wrap">
                            {t("f_cu_terms_header_1")}
                          </TableHead>
                          <TableHead variant={"public"} className="body-md-bold text-center whitespace-pre-wrap">
                            {t("f_cu_terms_header_2")}
                          </TableHead>
                          <TableHead variant={"public"} className="body-md-bold text-center whitespace-pre-wrap">
                            {t("f_cu_terms_header_3")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody className="body-md-regular text-center text-gray-500">
                        <TableRow className="h-[46px] border-gray-100">
                          <TableCell className="whitespace-pre-wrap">{t("f_cu_terms_context_1")}</TableCell>
                          <TableCell className="whitespace-pre-wrap">{t("f_cu_terms_context_2")}</TableCell>
                          <TableCell className="whitespace-pre-wrap">{t("f_cu_terms_context_3")}</TableCell>
                        </TableRow>
                        <TableRow className="h-[46px] border-gray-100">
                          <TableCell className="whitespace-pre-wrap">{t("f_cu_terms_context_4")}</TableCell>
                          <TableCell className="whitespace-pre-wrap">{t("f_cu_terms_context_5")}</TableCell>
                          <TableCell className="whitespace-pre-wrap">{t("f_cu_terms_context_6")}</TableCell>
                        </TableRow>
                      </TableBody>
                      <TableCaption className="body-sm-regular mt-3 text-start text-gray-500">
                        <div className="mb-1 flex gap-[6px]">
                          <span>※</span>
                          <span>{t("f_cu_terms_note_1")}</span>
                        </div>
                        <div className="flex gap-[6px]">
                          <span>※</span>
                          <span>{t("f_cu_terms_note_2")}</span>
                        </div>
                      </TableCaption>
                    </Table>

                    <div className="mt-4 flex items-start gap-3">
                      <div className="body-md-bold text-gray-700">{t("f_cu_terms_fot_1")}</div>
                      <div className="body-md-regular flex flex-col text-gray-700">
                        <span>{t("f_cu_terms_fot_2")}</span>
                        <span>{t("f_cu_terms_fot_3")}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-10 flex justify-center">
                <Button type="submit" variant="primary" size="lg" disabled={isPending} className="w-full">
                  {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("f_cu_btn")}
                </Button>
              </div>
            </div>

            <div className="tb:block hidden">
              <div className="pc:rounded-24 pc:border pc:border-gray-100 pc:p-15 space-y-8">
                <div className="pc:justify-between tb:flex-col pc:flex-row flex gap-8">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="flex w-full !gap-0">
                        <FormLabel className="body-lg-bold mr-2 flex min-w-[170px] items-start gap-[2px] py-[13.5px] whitespace-nowrap text-gray-900">
                          {t("f_cu_lable_name")}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <div className="w-full">
                          <FormControl>
                            <Input
                              placeholder={t("f_cu_placeholder_name")}
                              {...field}
                              variant="public"
                              maxLength={50}
                            />
                          </FormControl>
                          <FormMessage className="mt-2" />
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact"
                    render={({ field }) => (
                      <FormItem className="flex w-full !gap-0">
                        <FormLabel className="body-lg-bold tb:min-w-[170px] pc:min-w-[100px] mr-2 flex items-start gap-[2px] py-[13.5px] whitespace-nowrap text-gray-900">
                          {t("f_cu_lable_phone")}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <div className="w-full">
                          <FormControl>
                            <Input
                              maxLength={50}
                              placeholder={t("f_cu_placeholder_phone")}
                              {...field}
                              variant="public"
                              onChange={(e) => {
                                field.onChange(e.target.value.replace(/[^0-9]/g, ""));
                              }}
                            />
                          </FormControl>
                          <FormMessage className="mt-2" />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="tb:flex-col pc:flex-row tb:gap-8 flex justify-between gap-16">
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem className="flex w-full !gap-0">
                        <FormLabel className="body-lg-bold mr-2 flex min-w-[170px] items-start gap-[2px] py-[13.5px] whitespace-nowrap text-gray-900">
                          {t("f_cu_lable_hosn")} <span className="text-red-500">*</span>
                        </FormLabel>
                        <div className="flex-1">
                          <FormControl>
                            <Input
                              placeholder={t("f_cu_placeholder_hosn")}
                              {...field}
                              variant="public"
                              maxLength={50}
                            />
                          </FormControl>
                          <FormMessage className="mt-2" />
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex w-full !gap-0">
                        <FormLabel className="body-lg-bold tb:min-w-[170px] pc:min-w-[100px] mr-2 flex items-start gap-[2px] py-[13.5px] whitespace-nowrap text-gray-900">
                          {t("f_cu_lable_E-mail")} <span className="text-red-500">*</span>
                        </FormLabel>
                        <div className="flex-1">
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={t("f_cu_placeholder_E-mail")}
                              {...field}
                              variant="public"
                            />
                          </FormControl>
                          <FormMessage className="mt-2" />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="flex w-full !gap-0">
                      <FormLabel className="body-lg-bold mr-2 flex min-w-[170px] items-start gap-[2px] py-[13.5px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_title")} <span className="text-red-500">*</span>
                      </FormLabel>
                      <div className="flex-1">
                        <FormControl>
                          <Input placeholder={t("f_cu_placeholder_title")} {...field} variant="public" />
                        </FormControl>
                        <FormMessage className="mt-2" />
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem className="flex w-full !gap-0">
                      <FormLabel className="body-lg-bold mr-2 flex min-w-[170px] items-start gap-[2px] py-[13.5px] whitespace-nowrap text-gray-900">
                        {t("f_cu_lable_context")} <span className="text-red-500">*</span>
                      </FormLabel>
                      <div className="flex-1">
                        <FormControl>
                          <Textarea placeholder={t("f_cu_placeholder_context")} className="resize-none" {...field} />
                        </FormControl>
                        <FormMessage className="mt-2" />
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex">
                  <div className="body-lg-bold mr-2 flex min-w-[170px] whitespace-pre-line text-gray-900">
                    {t("f_cu_lable_atc")}
                  </div>
                  <div className="flex-1 space-y-3">
                    <FormField
                      control={form.control}
                      name="agreePrivacy"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start">
                          <FormControl>
                            <Checkbox
                              variant="public"
                              className="mt-[2px]"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="flex w-full items-center justify-between">
                            <FormLabel className="body-md-regular text-gray-900">{t("f_cu_atc_1")}</FormLabel>

                            <button
                              type="button"
                              onClick={() => setShowTermsContent(!showTermsContent)}
                              className="body-md-regular flex items-center gap-[6px] text-gray-900"
                            >
                              <Ico20ArrowRight
                                className={`transition-transform ${showTermsContent ? "rotate-[-90deg]" : "rotate-[90deg]"}`}
                              />
                            </button>
                          </div>
                        </FormItem>
                      )}
                    />

                    {showTermsContent && (
                      <div className="bg-secondary-100 animate-in fade-in-0 fade-out-0 mt-5 rounded-2xl p-7.5 duration-300">
                        <div className="body-md-regular text-gray-700">{t("f_cu_terms_title")}</div>

                        <Table className="mt-6 max-w-[860px] bg-white">
                          <TableHeader className="body-md-bold h-[46px] border-b border-gray-900 text-gray-900">
                            <TableRow className="border-gray-900">
                              <TableHead variant={"public"} className="body-md-bold text-center">
                                {t("f_cu_terms_header_1")}
                              </TableHead>
                              <TableHead variant={"public"} className="body-md-bold text-center">
                                {t("f_cu_terms_header_2")}
                              </TableHead>
                              <TableHead variant={"public"} className="body-md-bold text-center">
                                {t("f_cu_terms_header_3")}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody className="body-md-regular text-center text-gray-500">
                            <TableRow className="h-[46px] border-gray-100">
                              <TableCell className="whitespace-pre-line">{t("f_cu_terms_context_1")}</TableCell>
                              <TableCell className="whitespace-pre-line">{t("f_cu_terms_context_2")}</TableCell>
                              <TableCell className="whitespace-pre-line">{t("f_cu_terms_context_3")}</TableCell>
                            </TableRow>
                            <TableRow className="h-[46px] border-gray-100">
                              <TableCell className="whitespace-pre-line">{t("f_cu_terms_context_4")}</TableCell>
                              <TableCell className="whitespace-pre-line">{t("f_cu_terms_context_5")}</TableCell>
                              <TableCell className="whitespace-pre-line">{t("f_cu_terms_context_6")}</TableCell>
                            </TableRow>
                          </TableBody>
                          <TableCaption className="body-sm-regular text-start text-gray-500">
                            <div className="mb-1 flex gap-[6px]">
                              <span>※</span>
                              <span>{t("f_cu_terms_note_1")}</span>
                            </div>
                            <div className="flex gap-[6px]">
                              <span>※</span>
                              <span>{t("f_cu_terms_note_2")}</span>
                            </div>
                          </TableCaption>
                        </Table>

                        <div className="mt-6 flex items-center gap-4">
                          <div className="body-md-bold text-gray-700">{t("f_cu_terms_fot_1")}</div>
                          <div className="body-md-regular flex items-center text-gray-700">
                            <span>{t("f_cu_terms_fot_2")}</span>
                            <div className="mx-3 h-4 w-px bg-gray-300" />
                            <span>{t("f_cu_terms_fot_3")}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {form.formState.errors.agreePrivacy && (
                      <div className="mt-[6px]">
                        <FormMessage>{t("f_cu_err_agree")}</FormMessage>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-10 flex justify-center">
                <Button type="submit" variant="primary" size="lg" disabled={isPending}>
                  {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("f_cu_btn")}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>

      <AlertDialog open={dialogState.show} onOpenChange={(open) => !open && handleDialogClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.has(dialogState.title) ? t(dialogState.title) : dialogState.title}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleDialogClose}>{t("f_com_alert_btn_01_01")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
